import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Alert,
} from '@mui/material';
import UserServices from '../../../features/userManagement/services/userService';
import { UserWithBarcode } from '../../../application/types/userWithBarcode';
import { LabelPrintJob } from '../types/LabelPrintJob';
import { useUserLabelGeneration } from '../hooks/useLabelGeneration';
import { calculateLabelSheets } from '../utils/labelFormatter';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalStore } from '../../../application/store/store';

interface UserLabelSelectorProps {
    /** Callback when label print job is created */
    onJobCreated: (job: LabelPrintJob) => void;
}

/**
 * UserLabelSelector component - allows selection of users for label printing.
 *
 * Fetches users via labelService, displays selection UI, and creates
 * LabelPrintJob when user confirms selection.
 */
export const UserLabelSelector: React.FC<UserLabelSelectorProps> = ({
    onJobCreated,
}) => {
    const { t } = useTranslation();
    const { profile } = useGlobalStore();
    const [users, setUsers] = useState<UserWithBarcode[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
        new Set()
    );
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const userService = new UserServices();

    // Fetch users on mount
    useEffect(() => {
        const loadUsers = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const fetchedUsers = await userService.getUsersWithBarcodes();
                setUsers(fetchedUsers);
            } catch (err) {
                setError(t('errors.errorLoadingUsers'));
                console.error('Error loading users:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadUsers();
    }, []);

    // Get selected users
    const selectedUsers = users.filter((user) => selectedUserIds.has(user.id));

    // Convert selected users to label items
    const labelItems = useUserLabelGeneration(selectedUsers);

    // Handle checkbox toggle
    const handleToggleUser = (userId: string) => {
        setSelectedUserIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectedUserIds.size === users.length) {
            setSelectedUserIds(new Set());
        } else {
            setSelectedUserIds(new Set(users.map((u) => u.id)));
        }
    };

    // Handle preview generation
    const handleGeneratePreview = () => {
        if (selectedUsers.length === 0) {
            return;
        }

        const job: LabelPrintJob = {
            id: uuidv4(),
            type: 'user',
            items: labelItems,
            createdAt: new Date(),
            createdBy: profile?.id || '',
            totalLabels: labelItems.length,
            totalSheets: calculateLabelSheets(labelItems.length),
            status: 'previewing',
        };

        onJobCreated(job);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    if (users.length === 0) {
        return (
            <Alert severity="info" sx={{ m: 2 }}>
                {t('labelPrinting.noUsersWithBarcodes')}
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('labelPrinting.selectUsers')}
            </Typography>

            <Box display="flex" justifyContent="space-between" mb={2}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={
                                selectedUserIds.size === users.length &&
                                users.length > 0
                            }
                            indeterminate={
                                selectedUserIds.size > 0 &&
                                selectedUserIds.size < users.length
                            }
                            onChange={handleSelectAll}
                        />
                    }
                    label={t('common.selectAll')}
                />
                <Typography variant="body2" color="textSecondary">
                    {t('common.selectedCount', {
                        count: selectedUserIds.size,
                    })}
                </Typography>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>{t('common.userName')}</TableCell>
                            <TableCell>{t('common.email')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id} hover>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedUserIds.has(user.id)}
                                        onChange={() =>
                                            handleToggleUser(user.id)
                                        }
                                    />
                                </TableCell>
                                <TableCell>{user.userName}</TableCell>
                                <TableCell>{user.email}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGeneratePreview}
                    disabled={selectedUserIds.size === 0}
                >
                    {t('labelPrinting.generatePreview')}
                </Button>
            </Box>

            {selectedUserIds.size > 0 && (
                <Typography variant="body2" color="textSecondary" mt={2}>
                    {t('labelPrinting.sheetsRequired', {
                        count: calculateLabelSheets(selectedUserIds.size),
                    })}
                </Typography>
            )}
        </Box>
    );
};
